const DB_NAME = 'heligraph';
const STORE = 'identity';
const KEY = 'browser_static_priv';
const RECONNECT_STORE = 'reconnect';
const RECONNECT_KEY = 'primary';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      if (!db.objectStoreNames.contains(RECONNECT_STORE)) db.createObjectStore(RECONNECT_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getStored(): Promise<Uint8Array | null> {
  const db = await openDb();
  try {
    return await new Promise<Uint8Array | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve((req.result as Uint8Array | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

async function putStored(priv: Uint8Array): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(priv, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/** Get or create the browser's persistent 32-byte X25519 static_priv. */
export async function getOrCreateBrowserIdentity(): Promise<Uint8Array> {
  const existing = await getStored();
  if (existing && existing.byteLength === 32) return existing;
  const priv = new Uint8Array(32);
  crypto.getRandomValues(priv);
  await putStored(priv);
  return priv;
}

export interface ReconnectRecord {
  agent_pub: Uint8Array;
  reconnect_root: Uint8Array;
}

export async function getStoredReconnectRecord(): Promise<ReconnectRecord | null> {
  const db = await openDb();
  try {
    return await new Promise<ReconnectRecord | null>((resolve, reject) => {
      const tx = db.transaction(RECONNECT_STORE, 'readonly');
      const req = tx.objectStore(RECONNECT_STORE).get(RECONNECT_KEY);
      req.onsuccess = () => {
        const v = req.result as ReconnectRecord | undefined;
        if (v && v.agent_pub?.byteLength === 32 && v.reconnect_root?.byteLength === 32) resolve(v);
        else resolve(null);
      };
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

export async function putStoredReconnectRecord(record: ReconnectRecord): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(RECONNECT_STORE, 'readwrite');
      tx.objectStore(RECONNECT_STORE).put(record, RECONNECT_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

export async function clearReconnectRecord(): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(RECONNECT_STORE, 'readwrite');
      tx.objectStore(RECONNECT_STORE).delete(RECONNECT_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}
